import type { JSX, ReactNode } from 'react'
import { BrandPanel } from './BrandPanel'

/**
 * The frame for every page reachable without a session.
 *
 * Two columns: the brand on the left (`BrandPanel` — AugRank's pitch on the
 * apex, the organisation's mark on its own host), the form on the right in a
 * card with its title above it. The reader may be on a phone, in a different
 * browser, or in a mail client's preview pane, and none of those has an
 * organisation selected for a sidebar to describe — so no shell, and under
 * 900px the panel folds to a strip and the form takes the width.
 *
 * **No theme toggle.** The public screens used to carry one so that "light and
 * dark both work, everywhere" held on the screens somebody lands on from an
 * email. They still do: the tokens follow `prefers-color-scheme` on their own,
 * so dark mode follows the OS here. What went is the *control* — a toggle on a
 * tenant's white-labelled login page is a piece of AugRank's chrome on a page
 * that is meant to carry none, and a stored preference is a thing the shell
 * offers once someone is inside.
 */
export function PublicPage({
  title,
  subtitle,
  children,
  footer,
}: {
  /**
   * Omitted on the two screens that title themselves inside the card.
   *
   * S-D2's screens 07 and 12 draw no `h1` above the card at all: the empty
   * state's own title *is* the page's title, and printing both gives the
   * reader two headings for one screen — "Check your inbox" above "Confirm
   * your email address" (AUTH-52, AUTH-74). Those screens render the heading
   * element themselves, so the document still starts at level 1.
   */
  title?: string
  /**
   * The line under the title (AUTH-33).
   *
   * `.sc-auth__head p` in the design — muted, body-sm, centred, and present on
   * screens 01, 05 and 08. It had no slot here at all, so each screen that
   * needed one had invented its own place for it: the OTP screen's sentence
   * had ended up inside the card body at full size, and the other two had
   * simply lost their line. A frame gap, closed in the frame.
   */
  subtitle?: ReactNode
  children: ReactNode
  footer?: ReactNode
}): JSX.Element {
  return (
    <main className="app-split">
      <BrandPanel />

      <section className="app-split__form">
        <div className="app-split__inner">
          {/*
            The title sits **above** the card, not in a header row inside it.

            `h1`, not `h3`: this is the page's own title — there is nothing above
            it on any of the twelve public screens — and it read `h3` until
            2026-07-30, so every one of them started its heading hierarchy at
            level 3 with no `h1` anywhere in the document.

            `design/screens/auth.html:155` puts the `h1` in a `.sc-auth__head`
            block *before* `.ar-card`, and `ar-card__header` does not appear once
            in the whole auth mockup — all eleven screens there are titled above
            the card, centred, at `--ar-text-h1-size`. §I V5 is that gap, on the
            product's most-seen screen, closed here rather than per screen
            because the deviation was never in a screen — it was in this
            component, identically on all twelve.
          */}
          {title !== undefined || subtitle !== undefined ? (
            <div className="app-public__head">
              {title ? <h1 className="app-public__title">{title}</h1> : null}
              {subtitle ? <p className="app-public__subtitle">{subtitle}</p> : null}
            </div>
          ) : null}

          <section className="ar-card app-public__card">{children}</section>

          {footer ? <div className="app-public__foot">{footer}</div> : null}
        </div>
      </section>
    </main>
  )
}
